import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Settings } from "lucide-react";
import Hero3D from "../components/Hero3D";
import ClientsGrid from "../components/ClientsGrid";
import EnquiryForm from "../components/EnquiryForm";
import LocationsMap from "../components/LocationsMap";
import BrandsTicker from "../components/BrandsTicker";
import HomeBlogSection from "../components/HomeBlogSection";
import AdminPanel from "../components/AdminPanel";
import Counter from "../components/Counter";
import Reveal from "../components/Reveal";
import { BRANDS, COMPANY } from "../lib/data";
import { trackEvent } from "../lib/analytics";

// Skip mounting the heavy WebGL Hero3D scene on small viewports — saves
// battery, JS parse cost, and prevents visual overlap with the headline.
const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 768px)").matches
      : true
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
};

const StatCard = ({ end, suffix, label, testId }) => (
  <div
    data-testid={testId}
    className="group relative overflow-hidden rounded-2xl border border-black/5 bg-white/70 p-7 backdrop-blur-xl transition-all duration-500 hover:-translate-y-1 hover:border-[#e85a1c]/30 hover:bg-white shadow-lux"
  >
    <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-[#fff1e8] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
    <div className="relative">
      <div className="font-display text-5xl tracking-tighter text-[#1c1c1e] md:text-6xl">
        <Counter end={end} suffix={suffix} testId={`${testId}-value`} />
      </div>
      <div className="mt-3 text-[11px] font-medium uppercase tracking-[0.25em] text-[#8b8b8f]">
        {label}
      </div>
      <div className="mt-5 h-px w-12 bg-[#e85a1c]" />
    </div>
  </div>
);

export default function Home() {
  const [adminOpen, setAdminOpen] = useState(false);
  const isDesktop = useIsDesktop();
  return (
    <div data-testid="home-page" className="grain bg-[#f7f7f5]">
      {/* Floating discreet admin trigger */}
      <button
        onClick={() => setAdminOpen(true)}
        data-testid="home-admin-trigger"
        className="fixed bottom-6 left-6 z-40 inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white/80 text-[#8b8b8f] backdrop-blur transition-all duration-300 hover:border-[#e85a1c] hover:text-[#e85a1c] hover:shadow-lux"
        aria-label="Admin"
        title="Admin access"
      >
        <Settings size={15} />
      </button>
      <AdminPanel open={adminOpen} onClose={() => setAdminOpen(false)} onChange={() => {}} />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-black/5 bg-gradient-to-b from-[#f7f7f5] to-[#efefe9]">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="absolute right-0 top-1/3 h-[600px] w-[600px] -translate-y-1/2 rounded-full bg-[#fff1e8] opacity-50 blur-3xl" />
        <div className="pointer-events-none absolute inset-y-0 right-0 hidden md:block md:w-[58%]">
          {isDesktop && <Hero3D />}
        </div>
        {/* Mobile: lightweight decorative accents instead of heavy 3D canvas */}
        <div className="pointer-events-none absolute inset-0 md:hidden overflow-hidden" aria-hidden="true">
          <div className="absolute -right-24 top-16 h-56 w-56 rounded-full bg-[#fff1e8] opacity-70 blur-3xl" />
          <div className="absolute -right-16 bottom-20 h-72 w-72 rounded-full bg-[#ffd6bd] opacity-40 blur-3xl" />
        </div>

        <div className="relative z-10 mx-auto flex min-h-[calc(100vh-72px)] max-w-[1400px] flex-col justify-center px-6 py-20 lg:px-10">
          <Reveal className="max-w-3xl">
            <div className="mb-6 inline-flex items-center gap-3 rounded-full border border-black/10 bg-white/80 px-4 py-2 text-[11px] uppercase tracking-[0.25em] text-[#4a4a4e] backdrop-blur">
              <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-[#e85a1c]" />
              50 years · Hyderabad&rsquo;s trusted TMT house
            </div>
            <h1
              data-testid="hero-headline"
              className="font-display text-[42px] uppercase leading-[0.9] tracking-tighter text-[#1c1c1e] sm:text-6xl md:text-7xl lg:text-[108px]"
            >
              Steel you<br />
              can build a<br />
              <span className="text-[#e85a1c]">legacy</span> on.
            </h1>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-[#4a4a4e] md:text-lg">
              <span className="font-semibold text-[#1c1c1e]">BAHETI STEEL TRADERS</span> — distributing the nation&rsquo;s most trusted TMT brands to home owners across Hyderabad and Secunderabad since {COMPANY.foundedYear}. Fair weight, live prices, on-time dispatch.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#enquiry"
                data-testid="hero-cta-enquiry"
                onClick={() => trackEvent("quote_click", { source: "home_hero" })}
                className="inline-flex items-center gap-3 rounded-full bg-[#e85a1c] px-7 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-white transition-all duration-300 hover:bg-[#c64812] hover:shadow-orange"
              >
                Send Enquiry <ArrowRight size={14} />
              </a>
              <Link
                to="/price"
                data-testid="hero-cta-prices"
                className="inline-flex items-center gap-3 rounded-full border border-black/15 bg-white/70 px-7 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-[#1c1c1e] backdrop-blur transition-all duration-300 hover:border-[#e85a1c] hover:text-[#e85a1c]"
              >
                See today&rsquo;s prices
              </Link>
            </div>
          </Reveal>

          <Reveal delay={200} className="mt-16 grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard end={COMPANY.legacyYears} suffix="+" label="Years of Legacy" testId="stat-years" />
            <StatCard end={500} suffix="+" label="Projects Served" testId="stat-projects" />
            <StatCard end={15000} suffix="+" label="Tonnage Annually" testId="stat-tonnage" />
          </Reveal>
        </div>
      </section>

      {/* Brand ticker — placed right above the "Only premium..." heading */}
      <BrandsTicker />

      {/* Brands */}
      <section className="border-b border-black/5 bg-white py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
            <div>
              <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
                / 01 Brands we deal in
              </div>
              <h2 className="mt-4 font-display text-4xl uppercase leading-[0.92] tracking-tighter text-[#1c1c1e] md:text-6xl">
                Only premium
                <br />TMT makes the cut.
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-[#4a4a4e]">
              From high-tensile Fe 600+ grades to mainstream Fe 500 — we
              stock what your project needs, in the tonnage you need.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {BRANDS.map((b, i) => (
              <Reveal
                key={b.name}
                delay={i * 70}
                data-testid={`brand-chip-${b.name.replace(/\s+/g, "-").toLowerCase()}`}
                className="group rounded-xl border border-black/5 bg-[#f7f7f5] p-5 transition-all duration-300 hover:-translate-y-1 hover:border-[#e85a1c]/40 hover:bg-white hover:shadow-lux-hover"
              >
                <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">
                  {b.grade}
                </div>
                <div className="mt-3 font-display text-lg uppercase tracking-tight text-[#1c1c1e]">
                  {b.name}
                </div>
                <div className="mt-4 h-px w-full bg-black/5 transition-all group-hover:bg-[#e85a1c]/30" />
                <div className="mt-3 text-xs uppercase tracking-wider text-[#8b8b8f]">
                  Available in 8 – 32 mm
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ClientsGrid />
      <LocationsMap />
      <HomeBlogSection />
      <EnquiryForm />
    </div>
  );
}
